import { currentLocale, intlLocale, locales, type Locale } from "./locale";

/**
 * An entity's names in the other languages, as the APIs send them (docs/languages-plan.md, rule 4):
 * `{ ar: { name: "مخبوزات" } }`. English is the entity's own field and the fallback; an empty translation reads in English.
 */
export type Translations = Partial<Record<string, Record<string, string>>>;

/** The languages content is translated into: every language but English (and the development pseudo-locale). */
export const contentLocales = (Object.keys(locales) as Locale[]).filter((l) => l !== "en" && l !== "pseudo");

/** A language's name in the language of the page: "Arabic" on an English page, "العربية" on an Arabic one. */
export function languageName(locale: Locale): string {
  try {
    return new Intl.DisplayNames([intlLocale()], { type: "language" }).of(locale) ?? locales[locale].name;
  } catch {
    return locales[locale].name;
  }
}

/** `field` in `locale`, or "" when it has none. */
export function translationOf(translations: Translations | undefined, locale: Locale, field: string): string {
  return translations?.[locale]?.[field] ?? "";
}

/** A copy with `field` in `locale` set to `value` (the server drops empty values). */
export function withTranslation(translations: Translations | undefined, locale: Locale, field: string, value: string): Translations {
  return { ...translations, [locale]: { ...translations?.[locale], [field]: value } };
}

/** Whether two sets of translations say the same thing, ignoring empty values. */
export function sameTranslations(a: Translations | undefined, b: Translations | undefined): boolean {
  return contentLocales.every((l) => Object.keys({ ...a?.[l], ...b?.[l] }).every((f) => translationOf(a, l, f).trim() === translationOf(b, l, f).trim()));
}

/** `field` in the page's language when the entity has it, else its English: what a reader sees of a translated name. */
export function inPageLanguage(english: string, translations: Translations | undefined, field = "name"): string {
  const locale = currentLocale();
  return (locale !== "en" && locale !== "pseudo" && translations?.[locale]?.[field]?.trim()) || english;
}
